import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Avatar,
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  IconButton,
  InputAdornment,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockResetIcon from "@mui/icons-material/LockReset";
import VisibilityOffOutlinedIcon from "@mui/icons-material/VisibilityOffOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import RestoreRoundedIcon from "@mui/icons-material/RestoreRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import { t } from "../i18n";
import { api } from "../apiClient";
import { htmlPreview, sanitizeBasicHtml } from "../htmlUtils";
import { SECTION_TERM_OPTIONS, sectionTerms as sectionTermsFor } from "../sectionTerms";
import {
  blockSx,
  dangerIconButtonSx,
  destructiveButtonSx,
  dialogSx as baseDialogSx,
  fieldSx as baseFieldSx,
  iconButtonSx,
  outlinedButtonSx,
  panelSx,
  primaryButtonSx,
  secondaryButtonSx,
  sectionLeadSx,
  sectionTitleSx,
  shellCardSx,
  staggerDelay,
  statusColors,
  uiMotion,
} from "../uiStyles";

const navItems = [
  { key: "general", label: { en: "General", ru: "Основные" } },
  { key: "interface", label: { en: "Interface", ru: "Интерфейс" } },
  { key: "environment", label: { en: "Environment", ru: "Окружение" } },
  { key: "users", label: { en: "Users", ru: "Пользователи" } },
  { key: "sections", label: { en: "Sections", ru: "Разделы" } },
  { key: "tasks", label: { en: "Tasks", ru: "Задачи" } },
  { key: "security", label: { en: "Security", ru: "Безопасность" } },
  { key: "email", label: { en: "Email", ru: "Эл. почта" } },
  { key: "notifications", label: { en: "Notifications", ru: "Уведомления" } },
  {
    key: "templates",
    label: { en: "Notification templates", ru: "Шаблоны уведомлений" },
  },
  { key: "deleted-tasks", label: { en: "Deleted tasks", ru: "Удалённые задачи" } },
];

const roleLabel = (role, locale) => ({
  admin: locale === "en" ? "Administrator" : "Администратор",
  owner: locale === "en" ? "Owner" : "Владелец",
  manager: locale === "en" ? "Manager" : "Руководитель",
  executor: locale === "en" ? "Executor" : "Исполнитель",
}[role] || role);

const roleOptions = (currentRole) =>
  currentRole === "admin"
    ? ["admin", "owner", "manager", "executor"]
    : ["owner", "manager", "executor"];

const dialogSx = {
  ...baseDialogSx,
  "& .MuiDialog-paper": {
    ...baseDialogSx["& .MuiDialog-paper"],
    width: "min(560px, calc(100vw - 32px))",
  },
};

const noSpinnerSx = {
  "& input[type=number]": { MozAppearance: "textfield" },
  "& input[type=number]::-webkit-outer-spin-button, & input[type=number]::-webkit-inner-spin-button":
    {
      WebkitAppearance: "none",
      margin: 0,
    },
};

const contentColumnSx = {
  width: "100%",
  maxWidth: { xs: "100%", xl: 1180 },
  mx: "auto",
};

const formBlockSx = {
  ...blockSx,
  width: "100%",
  p: { xs: 1.75, md: 2.25 },
  mx: "auto",
};

const formFieldWrapSx = {
  width: "100%",
};

function AdminNavList({ items, activeKey, locale, layoutId, onSelect }) {
  return (
    <Stack spacing={0.5}>
      {items.map((item) => {
        const active = item.key === activeKey;
        return (
          <Button
            key={item.key}
            type="button"
            onClick={() => onSelect(item.key)}
            sx={{
              ...secondaryButtonSx,
              position: "relative",
              justifyContent: "flex-start",
              borderRadius: 3,
              minHeight: 42,
              fontWeight: 500,
              color: active ? "primary.contrastText" : "text.primary",
              "&:hover": { bgcolor: active ? "transparent" : "action.hover" },
            }}
          >
            {active && (
              <Box
                component={motion.span}
                layoutId={layoutId}
                transition={{ type: "spring", stiffness: 420, damping: 36 }}
                sx={{ position: "absolute", inset: 0, borderRadius: 3, bgcolor: "primary.main", zIndex: 0 }}
              />
            )}
            <Box component="span" sx={{ position: "relative", zIndex: 1 }}>
              {item.label[locale === "en" ? "en" : "ru"]}
            </Box>
          </Button>
        );
      })}
    </Stack>
  );
}

function ConfirmActionDialog({
  open,
  title,
  message,
  confirmLabel,
  onClose,
  onConfirm,
  busy,
  currentLocale,
  currentTheme,
  destructive = true,
}) {
  return (
    <Dialog
      open={open}
      onClose={busy ? undefined : onClose}
      fullWidth
      maxWidth="xs"
      sx={dialogSx}
    >
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {title}
        </Typography>
      </DialogTitle>
      <DialogContent>
        <Typography color="text.secondary">{message}</Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button
          onClick={onClose}
          disabled={busy}
          sx={secondaryButtonSx}
        >
          {currentLocale === "en" ? "Cancel" : "Отмена"}
        </Button>
        <Button
          variant="contained"
          color={destructive ? "error" : "primary"}
          onClick={onConfirm}
          disabled={busy}
          sx={destructive ? destructiveButtonSx : primaryButtonSx}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

function deletedTaskStatus(task, en) {
  if (task.archived) return { label: en ? "Archived" : "В архиве", color: "warning.main" };
  if (task.status === "done") {
    return task.completedLate
      ? { label: en ? "Completed late" : "Выполнено с нарушением срока", color: statusColors.completedLate }
      : { label: en ? "Completed" : "Выполнено", color: statusColors.completed };
  }
  if (task.overdue) return { label: en ? "Overdue" : "Просрочено", color: statusColors.overdue };
  return task.status === "in_progress"
    ? { label: en ? "In progress" : "В работе", color: statusColors.active }
    : { label: en ? "Assigned" : "Назначена", color: statusColors.assigned };
}

const roundIconSx = { borderRadius: 999, width: 42, height: 42 };

function DeletedTaskDialog({ taskId, currentLocale, currentRole, busy, onClose, onRestore, onPurge }) {
  const en = currentLocale === "en";
  const [task, setTask] = useState(null);
  const [error, setError] = useState("");
  const formatMoment = (value) => (value ? new Date(value).toLocaleString(en ? "en-US" : "ru-RU", { dateStyle: "short", timeStyle: "short" }) : "—");

  useEffect(() => {
    if (!taskId) return;
    setTask(null);
    setError("");
    api.get(`/trash/tasks/${taskId}`)
      .then((response) => setTask(response.data))
      .catch((requestError) => setError(requestError?.response?.data?.error || requestError?.message || (en ? "Could not load task" : "Не удалось загрузить задачу")));
  }, [taskId]); // eslint-disable-line react-hooks/exhaustive-deps

  const status = task ? deletedTaskStatus(task, en) : null;
  const facts = task ? [
    [en ? "Initiator" : "Инициатор", task.creatorName],
    [en ? "Executor" : "Исполнитель", task.assigneeName],
    [en ? "Due" : "Срок", formatMoment(task.dueAt)],
    [en ? "Created" : "Создана", formatMoment(task.createdAt)],
    [en ? "Deleted" : "Удалена", `${formatMoment(task.deletedAt)}${task.deletedByName ? ` · ${task.deletedByName}` : ""}`],
  ] : [];

  return (
    <Dialog open={!!taskId} onClose={busy ? undefined : onClose} fullWidth maxWidth="md" sx={dialogSx}>
      <DialogTitle sx={{ pb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 700, overflowWrap: "anywhere" }}>
          {task?.title || (en ? "Deleted task" : "Удалённая задача")}
        </Typography>
        {status && (
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
            <Chip size="small" label={status.label} sx={{ bgcolor: status.color, color: "#fff", fontWeight: 600 }} />
            {(task.sections || []).map((section) => (
              <Chip key={section.id ?? section.name ?? section} size="small" variant="outlined" label={section.name ?? section} />
            ))}
          </Stack>
        )}
      </DialogTitle>
      <DialogContent>
        {error && <Alert severity="error">{error}</Alert>}
        {!task && !error && <LinearProgress />}
        {task && (
          <Stack spacing={2}>
            <Box sx={{ ...blockSx, p: 1.75, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, minmax(0, 1fr))" }, gap: 1.25 }}>
              {facts.map(([label, value]) => (
                <Box key={label} sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary">{label}</Typography>
                  <Typography variant="body2" sx={{ overflowWrap: "anywhere" }}>{value || "—"}</Typography>
                </Box>
              ))}
            </Box>
            <Box sx={{ ...blockSx, p: 1.75 }}>
              <Typography sx={{ fontWeight: 700, mb: 1 }}>{en ? "Task description" : "Описание задачи"}</Typography>
              {task.description ? (
                <Box sx={{ fontSize: 15, lineHeight: 1.7, "& p": { my: 0.6 } }} dangerouslySetInnerHTML={{ __html: sanitizeBasicHtml(task.description) }} />
              ) : (
                <Typography variant="body2" color="text.secondary">{en ? "No description" : "Описание не добавлено"}</Typography>
              )}
            </Box>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Button onClick={onClose} disabled={busy} sx={secondaryButtonSx}>{en ? "Close" : "Закрыть"}</Button>
        {currentRole === "admin" && <Button variant="contained" color="error" onClick={() => onPurge(task)} disabled={busy || !task} sx={destructiveButtonSx}>{en ? "Delete" : "Удалить"}</Button>}
        <Button variant="contained" onClick={() => onRestore(task)} disabled={busy || !task} sx={primaryButtonSx}>{en ? "Restore" : "Восстановить"}</Button>
      </DialogActions>
    </Dialog>
  );
}

function DeletedTasksSection({ currentLocale, currentRole, currentTheme }) {
  const en = currentLocale === "en";
  const isAdmin = currentRole === "admin";
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [pendingAction, setPendingAction] = useState(null);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState([]);
  const [viewId, setViewId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const response = await api.get("/trash/tasks");
      setTasks(response.data || []);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || requestError?.message || (en ? "Could not load deleted tasks" : "Не удалось загрузить удалённые задачи"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const needle = query.trim().toLowerCase();
  const visibleTasks = needle
    ? tasks.filter((task) => [String(task.id), task.title, htmlPreview(task.description), task.creatorName, task.assigneeName]
        .some((value) => String(value || "").toLowerCase().includes(needle)))
    : tasks;
  const visibleIds = visibleTasks.map((task) => task.id);
  const selectedVisible = selected.filter((id) => visibleIds.includes(id));
  const allSelected = visibleIds.length > 0 && selectedVisible.length === visibleIds.length;
  const toggle = (id) => setSelected((items) => (items.includes(id) ? items.filter((item) => item !== id) : [...items, id]));
  const toggleAll = () => setSelected((items) => (allSelected
    ? items.filter((id) => !visibleIds.includes(id))
    : [...new Set([...items, ...visibleIds])]));
  const dropTasks = (ids) => {
    setTasks((items) => items.filter((item) => !ids.includes(item.id)));
    setSelected((items) => items.filter((id) => !ids.includes(id)));
    if (ids.includes(viewId)) setViewId(null);
  };

  const restore = async (task) => {
    setBusyId(task.id);
    setError("");
    try {
      await api.post(`/trash/tasks/${task.id}/restore`);
      dropTasks([task.id]);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || requestError?.message || (en ? "Could not restore task" : "Не удалось восстановить задачу"));
    } finally {
      setBusyId(null);
    }
  };

  const purge = async (ids) => {
    setBusyId(ids.length === 1 ? ids[0] : "selected");
    setError("");
    try {
      if (ids.length === 1) await api.delete(`/trash/tasks/${ids[0]}`);
      else await api.post("/trash/tasks/purge", { ids });
      dropTasks(ids);
    } catch (requestError) {
      setError(requestError?.response?.data?.error || requestError?.message || (en ? "Could not delete tasks" : "Не удалось удалить задачи"));
    } finally {
      setBusyId(null);
    }
  };

  const confirmPendingAction = async () => {
    const action = pendingAction;
    if (!action) return;
    if (action.type === "restore") await restore(action.task);
    if (action.type === "purge") await purge([action.task.id]);
    if (action.type === "purgeSelected") await purge(action.ids);
    setPendingAction(null);
  };

  const confirmation = pendingAction?.type === "restore"
    ? {
        title: en ? "Restore task?" : "Восстановить задачу?",
        message: en
          ? `Task “${pendingAction.task.title}” will return to the task list`
          : `Задача «${pendingAction.task.title}» вернётся в список задач`,
        label: en ? "Restore" : "Восстановить",
        destructive: false,
      }
    : pendingAction?.type === "purge"
      ? {
          title: en ? "Delete task?" : "Удалить задачу?",
          message: en
            ? `Task “${pendingAction.task.title}” and all related data will be permanently deleted`
            : `Задача «${pendingAction.task.title}» и все связанные данные будут удалены без возможности восстановления`,
          label: en ? "Delete" : "Удалить",
          destructive: true,
        }
      : {
          title: pendingAction?.ids?.length === tasks.length
            ? (en ? "Delete all tasks?" : "Удалить все задачи?")
            : (en ? "Delete selected tasks?" : "Удалить выбранные задачи?"),
          message: pendingAction?.ids?.length === tasks.length
            ? (en
                ? "All deleted tasks and their related data will be permanently deleted"
                : "Все удалённые задачи и связанные с ними данные будут удалены без возможности восстановления")
            : en
              ? `Selected tasks (${pendingAction?.ids?.length || 0}) and their related data will be permanently deleted`
              : `Выбранные задачи (${pendingAction?.ids?.length || 0}) и связанные с ними данные будут удалены без возможности восстановления`,
          label: en ? "Delete" : "Удалить",
          destructive: true,
        };

  return (
    <Paper elevation={0} sx={{ ...panelSx, width: "100%", maxWidth: { xs: "100%", xl: 1180 }, mx: "auto" }}>
      <Box>
        <Typography variant="h6" sx={sectionTitleSx}>{en ? "Deleted tasks" : "Удалённые задачи"}</Typography>
        <Typography variant="body2" sx={sectionLeadSx}>{en ? "Restore tasks or permanently delete them" : "Восстановление и окончательное удаление задач"}</Typography>
      </Box>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {loading ? <LinearProgress /> : tasks.length === 0 ? (
        <Typography color="text.secondary" sx={{ py: 5, textAlign: "center" }}>{en ? "No deleted tasks" : "Удалённых задач нет"}</Typography>
      ) : (
        <Stack spacing={1.25}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems={{ xs: "stretch", sm: "center" }} justifyContent="space-between" sx={{ mb: 0.5 }}>
            {isAdmin ? (
              <Box sx={{ pl: "15px" }}>
                <FormControlLabel
                  control={<Checkbox checked={allSelected} indeterminate={selectedVisible.length > 0 && !allSelected} onChange={toggleAll} disabled={!visibleIds.length || busyId !== null} />}
                  label={en ? "Select all" : "Выбрать все"}
                  sx={{ ml: 0, mr: 0 }}
                />
              </Box>
            ) : <Box />}
            <TextField
              size="small"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={en ? "Search" : "Поиск"}
              inputProps={{ "aria-label": en ? "Search" : "Поиск" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchRoundedIcon sx={{ fontSize: 20, color: "text.secondary" }} />
                  </InputAdornment>
                ),
              }}
              sx={{ ...baseFieldSx(currentTheme === "dark"), width: { xs: "100%", sm: 270 }, "& .MuiOutlinedInput-root": { borderRadius: 999 } }}
            />
          </Stack>
          {visibleTasks.length === 0 && (
            <Typography color="text.secondary" sx={{ py: 4, textAlign: "center" }}>{en ? "Nothing found" : "Ничего не найдено"}</Typography>
          )}
          {visibleTasks.map((task, index) => (
            <Box key={task.id} component={motion.div} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: staggerDelay(index), duration: uiMotion.base, ease: uiMotion.ease }} onClick={() => { if (busyId !== null) return; if (isAdmin) toggle(task.id); else setViewId(task.id); }} sx={{ ...blockSx, p: 1.75, cursor: "pointer" }}>
              <Stack direction="row" spacing={1.5} alignItems="center" justifyContent="space-between">
                <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                  {isAdmin && (
                    <Checkbox
                      checked={selected.includes(task.id)}
                      onChange={() => toggle(task.id)}
                      onClick={(event) => event.stopPropagation()}
                      disabled={busyId !== null}
                      inputProps={{ "aria-label": task.title }}
                      sx={{ ml: -0.75 }}
                    />
                  )}
                  <Box sx={{ minWidth: 0 }}>
                    <Typography sx={{ fontWeight: 600, overflowWrap: "anywhere" }}>{task.title}</Typography>
                    <Typography variant="caption" color="text.secondary">{en ? "Deleted" : "Удалена"}: {task.deletedAt ? new Date(task.deletedAt).toLocaleString(en ? "en-US" : "ru-RU") : "—"}</Typography>
                  </Box>
                </Stack>
                <Stack direction="row" spacing={1} sx={{ flexShrink: 0 }} onClick={(event) => event.stopPropagation()}>
                  <Tooltip title={en ? "View" : "Просмотр"} arrow>
                    <span>
                      <IconButton onClick={() => setViewId(task.id)} disabled={busyId !== null} sx={{ ...iconButtonSx, ...roundIconSx }}>
                        <VisibilityOutlinedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title={en ? "Restore" : "Восстановить"} arrow>
                    <span>
                      <IconButton onClick={() => setPendingAction({ type: "restore", task })} disabled={busyId !== null} sx={{ ...iconButtonSx, ...roundIconSx }}>
                        <RestoreRoundedIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  {isAdmin && (
                    <Tooltip title={en ? "Delete" : "Удалить"} arrow>
                      <span>
                        <IconButton color="error" onClick={() => setPendingAction({ type: "purge", task })} disabled={busyId !== null} sx={{ ...dangerIconButtonSx, ...roundIconSx }}>
                          <DeleteOutlineIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                </Stack>
              </Stack>
            </Box>
          ))}
          <AnimatePresence initial={false}>
            {isAdmin && selected.length > 0 && (
              <Box component={motion.div} key="purge-selected" initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 4 }} transition={{ duration: uiMotion.fast, ease: uiMotion.ease }} sx={{ alignSelf: "flex-start", mt: 1 }}>
                <Button color="error" variant="contained" onClick={() => setPendingAction({ type: "purgeSelected", ids: selected })} disabled={busyId !== null} sx={destructiveButtonSx}>
                  {selected.length === tasks.length ? (en ? "Delete all" : "Удалить все") : `${en ? "Delete" : "Удалить"} (${selected.length})`}
                </Button>
              </Box>
            )}
          </AnimatePresence>
        </Stack>
      )}
      <DeletedTaskDialog
        taskId={viewId}
        currentLocale={currentLocale}
        currentRole={currentRole}
        busy={busyId !== null}
        onClose={() => setViewId(null)}
        onRestore={(task) => setPendingAction({ type: "restore", task })}
        onPurge={(task) => setPendingAction({ type: "purge", task })}
      />
      <ConfirmActionDialog
        open={!!pendingAction}
        title={confirmation.title}
        message={confirmation.message}
        confirmLabel={confirmation.label}
        onClose={() => setPendingAction(null)}
        onConfirm={confirmPendingAction}
        busy={busyId !== null}
        currentLocale={currentLocale}
        currentTheme={currentTheme}
        destructive={confirmation.destructive}
      />
    </Paper>
  );
}

function SectionsSection({
  currentLocale,
  currentTheme,
  sections = [],
  terms,
  defaultSectionEnabled = true,
  onChanged,
}) {
  const [form, setForm] = useState({ name: "", description: "", isDefault: false });
  const [editingId, setEditingId] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [sectionToRemove, setSectionToRemove] = useState(null);
  const en = currentLocale === "en";
  const save = async () => {
    setBusy(true);
    setError("");
    try {
      if (editingId) await api.put(`/admin/sections/${editingId}`, form);
      else await api.post("/admin/sections", form);
      setForm({ name: "", description: "", isDefault: false });
      setEditingId(null);
      setDialogOpen(false);
      await onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };
  const openCreate = () => {
    setEditingId(null);
    setForm({ name: "", description: "", isDefault: false });
    setError("");
    setDialogOpen(true);
  };
  const openEdit = (section) => {
    setEditingId(section.id);
    setForm({ name: section.name, description: section.description || "", isDefault: !!section.isDefault });
    setError("");
    setDialogOpen(true);
  };
  const remove = async () => {
    if (!sectionToRemove) return;
    setBusy(true);
    setError("");
    try {
      await api.delete(`/admin/sections/${sectionToRemove.id}`);
      await onChanged?.();
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    } finally {
      setSectionToRemove(null);
      setBusy(false);
    }
  };
  return (
    <Paper
      elevation={0}
      sx={{
        ...panelSx,
        width: "100%",
        maxWidth: { xs: "100%", xl: 1180 },
        mx: "auto",
      }}
    >
      <Box
        sx={{
          ...contentColumnSx,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={sectionTitleSx}>
            {terms.plural}
          </Typography>
          <Typography variant="body2" sx={sectionLeadSx}>
            {en
              ? "Directory that tasks belong to. Its name is set under Environment"
              : "Справочник, к которому относятся задачи. Название настраивается в разделе «Окружение»"}
          </Typography>
        </Box>
        <Tooltip title={terms.newItem} arrow>
          <IconButton
            onClick={openCreate}
            sx={{
              ...iconButtonSx,
              borderRadius: 999,
              color: "text.primary",
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      <Stack spacing={1.5} sx={contentColumnSx}>
        {sections.map((section, index) => (
          <Box
            key={section.id}
            component={motion.div}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: staggerDelay(index), duration: uiMotion.base, ease: uiMotion.ease }}
            sx={{
              ...blockSx,
              borderRadius: 3,
              p: { xs: 1.25, sm: 1.5 },
              bgcolor: "background.default",
            }}
          >
            <Stack
              direction={{ xs: "column", md: "row" }}
              spacing={1.5}
              justifyContent="space-between"
              alignItems={{ xs: "stretch", md: "center" }}
            >
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="flex-start"
                sx={{ minWidth: 0, flex: 1 }}
              >
                <Avatar
                  sx={{
                    width: { xs: 40, sm: 44 },
                    height: { xs: 40, sm: 44 },
                    bgcolor: "ui.avatar",
                    color: "#fff",
                    fontSize: 16,
                    fontWeight: 700,
                  }}
                >
                  {section.name.charAt(0).toUpperCase()}
                </Avatar>
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      lineHeight: 1.2,
                      wordBreak: "break-word",
                    }}
                  >
                    {section.name}
                  </Typography>
                  {section.description && (
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        mt: 0.35,
                        wordBreak: "break-word",
                      }}
                    >
                      {section.description}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap sx={{ mt: 0.85 }}>
                    <Chip
                      size="small"
                      label={
                        en
                          ? `${section.taskCount} tasks`
                          : `Задач: ${section.taskCount}`
                      }
                    />
                    {defaultSectionEnabled && section.isDefault && (
                      <Chip
                        size="small"
                        color="primary"
                        label={en ? "Default" : "По умолчанию"}
                      />
                    )}
                  </Stack>
                </Box>
              </Stack>
              <Stack direction="row" spacing={1}>
                <Tooltip title={terms.edit} arrow>
                  <span>
                    <IconButton
                      disabled={busy}
                      onClick={() => openEdit(section)}
                      sx={{
                        ...iconButtonSx,
                        borderRadius: 999,
                        width: { xs: "100%", sm: 42 },
                        height: 42,
                      }}
                    >
                      <EditOutlinedIcon />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title={terms.remove} arrow>
                  <span>
                    <IconButton
                      color="error"
                      disabled={busy}
                      onClick={() => setSectionToRemove(section)}
                      sx={{
                        ...dangerIconButtonSx,
                        borderRadius: 999,
                        width: { xs: "100%", sm: 42 },
                        height: 42,
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>
        ))}
        {!sections.length && (
          <Typography color="text.secondary">
            {terms.empty}
          </Typography>
        )}
      </Stack>
      <Dialog
        open={dialogOpen}
        onClose={busy ? undefined : () => setDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        sx={dialogSx}
      >
        <DialogTitle sx={{ pb: 0.5 }}>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {editingId ? terms.edit : terms.newItem}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.75, mb: 2 }}
          >
            {terms.dialogLead}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ display: "grid", gap: 2, pt: 0.5 }}>
          <TextField
            autoFocus
            fullWidth
            margin="normal"
            sx={{ ...baseFieldSx(currentTheme === "dark"), mt: 1.5 }}
            label={en ? "Name" : "Название"}
            value={form.name}
            onChange={(e) => setForm((v) => ({ ...v, name: e.target.value }))}
          />
          <TextField
            fullWidth
            sx={baseFieldSx(currentTheme === "dark")}
            label={en ? "Description" : "Описание"}
            multiline
            minRows={3}
            value={form.description}
            onChange={(e) =>
              setForm((v) => ({ ...v, description: e.target.value }))
            }
          />
          {defaultSectionEnabled && <Box sx={formBlockSx}>
            <FormControlLabel
              sx={{ m: 0, width: "100%", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}
              labelPlacement="start"
              control={
                <Switch
                  checked={!!form.isDefault}
                  disabled={busy}
                  onChange={(e) =>
                    setForm((v) => ({ ...v, isDefault: e.target.checked }))
                  }
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>
                    {terms.defaultLabel}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                    {terms.defaultHint}
                  </Typography>
                </Box>
              }
            />
          </Box>}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              gap: 1.25,
              width: "100%",
            }}
          >
            <Button
              onClick={() => setDialogOpen(false)}
              disabled={busy}
              sx={secondaryButtonSx}
            >
              {en ? "Cancel" : "Отмена"}
            </Button>
            <Button
              variant="contained"
              disabled={busy || !form.name.trim()}
              onClick={save}
              sx={primaryButtonSx}
            >
              {editingId
                ? en
                  ? "Save"
                  : "Сохранить"
                : en
                  ? "Create"
                  : "Создать"}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>
      <ConfirmActionDialog
        open={!!sectionToRemove}
        title={terms.removeQuestion}
        message={
          sectionToRemove ? terms.removeMessage(sectionToRemove.name) : ""
        }
        confirmLabel={en ? "Delete" : "Удалить"}
        onClose={() => setSectionToRemove(null)}
        onConfirm={remove}
        busy={busy}
        currentLocale={currentLocale}
        currentTheme={currentTheme}
      />
    </Paper>
  );
}

function CreateUserDialog({
  open,
  currentLocale,
  currentTheme = "light",
  form,
  onClose,
  onChange,
  onCreate,
  loading,
  currentRole,
}) {
  const isDark = currentTheme === "dark";
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      sx={dialogSx}
    >
      <DialogTitle sx={{ pb: 0.5 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t("admin.newUser", currentLocale)}
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: "text.secondary", mt: 0.75, mb: 2 }}
        >
          {t("admin.addUserText", currentLocale)}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2, pt: 0.5 }}>
        <TextField
          required
          label={currentLocale === "en" ? "Last name" : "Фамилия"}
          value={form.lastName || ""}
          onChange={(e) => onChange("lastName", e.target.value)}
          fullWidth
          margin="normal"
          sx={{ ...baseFieldSx(isDark), mt: 1.5 }}
        />
        <TextField
          required
          label={currentLocale === "en" ? "First name" : "Имя"}
          value={form.firstName || ""}
          onChange={(e) => onChange("firstName", e.target.value)}
          fullWidth
          sx={baseFieldSx(isDark)}
        />
        <TextField
          label={currentLocale === "en" ? "Middle name" : "Отчество"}
          value={form.middleName || ""}
          onChange={(e) => onChange("middleName", e.target.value)}
          fullWidth
          sx={baseFieldSx(isDark)}
        />
        <TextField
          label={currentLocale === "en" ? "Position" : "Должность"}
          value={form.position || ""}
          onChange={(e) => onChange("position", e.target.value)}
          fullWidth
          sx={baseFieldSx(isDark)}
        />
        <TextField
          multiline
          minRows={2}
          label={currentLocale === "en" ? "About" : "Описание"}
          value={form.description || ""}
          onChange={(e) => onChange("description", e.target.value)}
          fullWidth
          sx={baseFieldSx(isDark)}
        />
        <TextField
          label={t("common.email", currentLocale)}
          value={form.email || ""}
          onChange={(e) => onChange("email", e.target.value)}
          fullWidth
          sx={baseFieldSx(isDark)}
        />
        <FormControl fullWidth sx={baseFieldSx(isDark)}>
          <InputLabel>{t("common.role", currentLocale)}</InputLabel>
          <Select
            label={t("common.role", currentLocale)}
            value={form.role || "executor"}
            onChange={(event) => onChange("role", event.target.value)}
          >
            {roleOptions(currentRole).map((role) => (
              <MenuItem key={role} value={role}>{roleLabel(role, currentLocale)}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControlLabel
          control={
            <Checkbox
              checked={!!form.sendInvitation}
              onChange={(e) => onChange("sendInvitation", e.target.checked)}
            />
          }
          label={
            currentLocale === "en"
              ? "Send invitation link by email"
              : "Отправить ссылку-приглашение по электронной почте"
          }
          sx={{ mx: 0 }}
        />
        {!form.sendInvitation ? (
          <>
            <TextField
              type="password"
              label={t("common.password", currentLocale)}
              value={form.password || ""}
              onChange={(e) => onChange("password", e.target.value)}
              fullWidth
              sx={baseFieldSx(isDark)}
            />
            <TextField
              type="password"
              label={t("admin.repeatPassword", currentLocale)}
              value={form.confirmPassword || ""}
              onChange={(e) => onChange("confirmPassword", e.target.value)}
              fullWidth
              sx={baseFieldSx(isDark)}
            />
          </>
        ) : null}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1.25,
            width: "100%",
          }}
        >
          <Button
            onClick={onClose}
            disabled={loading}
            sx={secondaryButtonSx}
          >
            {t("common.cancel", currentLocale)}
          </Button>
          <Button
            variant="contained"
            onClick={onCreate}
            disabled={
              loading ||
              !String(form.lastName || "").trim() ||
              !String(form.firstName || "").trim() ||
              !String(form.email || "").trim() ||
              (!form.sendInvitation &&
                (!form.password || form.password !== form.confirmPassword))
            }
            sx={primaryButtonSx}
          >
            {t("common.create", currentLocale)}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

function PasswordDialog({
  open,
  currentLocale,
  currentTheme = "light",
  form,
  onClose,
  onPasswordChange,
  onConfirmChange,
  onSave,
  loading,
}) {
  const isDark = currentTheme === "dark";
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      sx={dialogSx}
    >
      <DialogTitle sx={{ pb: 1.25 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t("admin.changePassword", currentLocale)}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75 }}>
          {t("admin.passwordDialogText", currentLocale)}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ display: "grid", gap: 2, pt: 1.75 }}>
        <Box
          sx={{
            px: 1.5,
            py: 1.15,
            borderRadius: 2.5,
            bgcolor: "ui.subtle",
            border: "1px solid", borderColor: "divider",
          }}
        >
          <Typography variant="body2" sx={{ color: "text.secondary" }}>
            {form.email || ""}
          </Typography>
        </Box>
        <TextField
          type="password"
          label={t("common.password", currentLocale)}
          value={form.password || ""}
          onChange={(e) => onPasswordChange(form.email, e.target.value)}
          fullWidth
          sx={baseFieldSx(isDark)}
        />
        <TextField
          type="password"
          label={t("admin.repeatPassword", currentLocale)}
          value={form.confirmPassword || ""}
          onChange={(e) => onConfirmChange(form.email, e.target.value)}
          fullWidth
          sx={baseFieldSx(isDark)}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 1 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1.25,
            width: "100%",
          }}
        >
          <Button
            onClick={onClose}
            disabled={loading}
            sx={secondaryButtonSx}
          >
            {t("common.cancel", currentLocale)}
          </Button>
          <Button
            variant="contained"
            onClick={onSave}
            disabled={loading}
            sx={primaryButtonSx}
          >
            {t("common.save", currentLocale)}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

function DeleteDialog({
  open,
  currentLocale,
  currentTheme = "light",
  email,
  onClose,
  onConfirm,
  loading,
}) {
  return (
    <Dialog
      open={open}
      onClose={loading ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      sx={dialogSx}
    >
      <DialogTitle sx={{ pb: 1.25 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          {t("admin.deleteUser", currentLocale)}
        </Typography>
        <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.75 }}>
          {t("admin.deleteDialogText", currentLocale)}
        </Typography>
      </DialogTitle>
      <DialogContent sx={{ pt: 1 }}>
        <Box
          sx={{
            px: 1.75,
            py: 1.4,
            borderRadius: 3,
            bgcolor: "ui.dangerSoft",
            border: "1px solid", borderColor: "ui.dangerBorder",
          }}
        >
          <Typography sx={{ mb: 0.5, fontWeight: 600 }}>
            {t("admin.deleteUserConfirm", currentLocale)}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", wordBreak: "break-word" }}
          >
            {email}
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, pt: 2 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "flex-end",
            alignItems: "center",
            gap: 1.25,
            width: "100%",
          }}
        >
          <Button
            onClick={onClose}
            disabled={loading}
            sx={secondaryButtonSx}
          >
            {t("common.cancel", currentLocale)}
          </Button>
          <Button
            onClick={onConfirm}
            variant="contained"
            disabled={loading}
            sx={destructiveButtonSx}
          >
            {t("admin.deleteUser", currentLocale)}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

function PickSelect({ label, value, options, onChange, disabled, isDark }) {
  return (
    <FormControl fullWidth sx={baseFieldSx(isDark)}>
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} disabled={disabled} onChange={(event) => onChange(event.target.value)}>
        {options.map((option) => (
          <MenuItem key={option.value} value={option.value}>
            {option.label}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

// Bulk operations over all tasks; only administrators see this section.
function TasksSection({ currentLocale, currentTheme, users = [], terms, onTasksChanged, onNotify }) {
  const en = currentLocale === "en";
  const isDark = currentTheme === "dark";
  const [tools, setTools] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [move, setMove] = useState({ from: "", to: "" });
  const [initiator, setInitiator] = useState({ from: "", to: "" });
  const [assignee, setAssignee] = useState({ from: "", to: "" });
  const load = async () => {
    try {
      setTools((await api.get("/admin/task-tools")).data);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
    }
  };
  useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const tasksLabel = (count) => `${en ? "tasks" : "задач"}: ${count}`;
  const personLabel = (item) =>
    `${item.name}${item.active ? "" : en ? " (inactive)" : " (неактивен)"} — ${tasksLabel(item.taskCount)}`;
  const userLabel = (user) => (user.displayName || "").trim() || user.email;
  const activeUsers = users.filter((user) => user.active !== false);
  const nameOf = (list, value, key = "email") => {
    const item = list.find((entry) => String(entry[key]) === String(value));
    return item ? item.name || userLabel(item) : "";
  };
  const ask = (action) => {
    setError("");
    setConfirm(action);
  };
  const run = async () => {
    setBusy(true);
    setError("");
    try {
      const response = await api.post(confirm.path, confirm.body || {});
      onNotify?.(`${confirm.done}: ${response.data.count}`);
      confirm.reset?.();
      setConfirm(null);
      await Promise.all([load(), onTasksChanged?.()]);
    } catch (e) {
      setError(e?.response?.data?.error || e.message);
      setConfirm(null);
    } finally {
      setBusy(false);
    }
  };
  const sections = tools?.sections || [];
  const initiators = tools?.initiators || [];
  const assignees = tools?.assignees || [];
  const initiatorTargets = activeUsers.filter(
    (user) => ["admin", "owner", "manager"].includes(user.role) && user.email !== initiator.from,
  );
  const assigneeTargets = activeUsers.filter((user) => user.email !== assignee.from);
  const moveCount = sections.find((section) => section.id === move.from)?.taskCount ?? 0;
  const transferNote = en
    ? "Tasks in the trash are moved too. A comment about the change is added to each task"
    : "Задачи в корзине тоже переносятся. В каждую задачу добавляется комментарий об изменении";

  const block = (title, description, controls, button) => (
    <Box sx={formBlockSx}>
      <Stack spacing={1.5}>
        <Box>
          <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
          <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
            {description}
          </Typography>
        </Box>
        {controls && (
          <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
            {controls}
          </Stack>
        )}
        <Box>{button}</Box>
      </Stack>
    </Box>
  );

  return (
    <Paper elevation={0} sx={{ ...panelSx, width: "100%", maxWidth: { xs: "100%", xl: 1180 }, mx: "auto" }}>
      <Typography variant="h6" sx={sectionTitleSx}>{en ? "Tasks" : "Задачи"}</Typography>
      <Typography variant="body2" sx={sectionLeadSx}>
        {en ? "Bulk operations on all tasks" : "Массовые операции со всеми задачами"}
      </Typography>
      <Stack spacing={2} sx={contentColumnSx}>
        {error && <Alert severity="error">{error}</Alert>}
        {!tools && !error && <LinearProgress />}
        {block(
          terms.moveTitle,
          en ? "Tasks in the trash are moved too" : "Задачи в корзине тоже переносятся",
          <>
            <PickSelect
              label={en ? "From" : "Откуда"}
              value={move.from}
              isDark={isDark}
              disabled={busy}
              options={sections.map((section) => ({ value: section.id, label: `${section.name} — ${tasksLabel(section.taskCount)}` }))}
              onChange={(value) => setMove({ from: value, to: move.to === value ? "" : move.to })}
            />
            <PickSelect
              label={en ? "To" : "Куда"}
              value={move.to}
              isDark={isDark}
              disabled={busy || move.from === ""}
              options={sections.filter((section) => section.id !== move.from).map((section) => ({ value: section.id, label: section.name }))}
              onChange={(value) => setMove({ ...move, to: value })}
            />
          </>,
          <Button
            variant="contained"
            sx={primaryButtonSx}
            disabled={busy || move.from === "" || move.to === ""}
            onClick={() =>
              ask({
                title: terms.moveTitle + "?",
                message: en
                  ? `Tasks of «${nameOf(sections, move.from, "id")}» move to «${nameOf(sections, move.to, "id")}»`
                  : `Задачи из «${nameOf(sections, move.from, "id")}» будут перенесены в «${nameOf(sections, move.to, "id")}»`,
                label: en ? "Move" : "Перенести",
                path: "/admin/tasks/move-section",
                body: { fromSectionId: move.from, toSectionId: move.to },
                done: en ? "Tasks moved" : "Перенесено задач",
                reset: () => setMove({ from: "", to: "" }),
                destructive: false,
              })
            }
          >
            {en ? `Move ${moveCount}` : `Перенести (${moveCount})`}
          </Button>,
        )}
        {block(
          en ? "Change the initiator" : "Сменить инициатора",
          transferNote,
          <>
            <PickSelect
              label={en ? "Current initiator" : "Текущий инициатор"}
              value={initiator.from}
              isDark={isDark}
              disabled={busy}
              options={initiators.map((item) => ({ value: item.email, label: personLabel(item) }))}
              onChange={(value) => setInitiator({ from: value, to: initiator.to === value ? "" : initiator.to })}
            />
            <PickSelect
              label={en ? "New initiator" : "Новый инициатор"}
              value={initiator.to}
              isDark={isDark}
              disabled={busy || !initiator.from}
              options={initiatorTargets.map((user) => ({ value: user.email, label: userLabel(user) }))}
              onChange={(value) => setInitiator({ ...initiator, to: value })}
            />
          </>,
          <Button
            variant="contained"
            sx={primaryButtonSx}
            disabled={busy || !initiator.from || !initiator.to}
            onClick={() =>
              ask({
                title: en ? "Change the initiator?" : "Сменить инициатора?",
                message: en
                  ? `All tasks of ${nameOf(initiators, initiator.from)} get the initiator ${nameOf(users, initiator.to)}`
                  : `У всех задач инициатора ${nameOf(initiators, initiator.from)} инициатором станет ${nameOf(users, initiator.to)}`,
                label: en ? "Change" : "Сменить",
                path: "/admin/initiators/reassign",
                body: { fromEmail: initiator.from, toEmail: initiator.to },
                done: en ? "Tasks updated" : "Изменено задач",
                reset: () => setInitiator({ from: "", to: "" }),
                destructive: false,
              })
            }
          >
            {en ? "Change" : "Сменить"}
          </Button>,
        )}
        {block(
          en ? "Change the executor" : "Сменить исполнителя",
          transferNote,
          <>
            <PickSelect
              label={en ? "Current executor" : "Текущий исполнитель"}
              value={assignee.from}
              isDark={isDark}
              disabled={busy}
              options={assignees.map((item) => ({ value: item.email, label: personLabel(item) }))}
              onChange={(value) => setAssignee({ from: value, to: assignee.to === value ? "" : assignee.to })}
            />
            <PickSelect
              label={en ? "New executor" : "Новый исполнитель"}
              value={assignee.to}
              isDark={isDark}
              disabled={busy || !assignee.from}
              options={assigneeTargets.map((user) => ({ value: user.email, label: userLabel(user) }))}
              onChange={(value) => setAssignee({ ...assignee, to: value })}
            />
          </>,
          <Button
            variant="contained"
            sx={primaryButtonSx}
            disabled={busy || !assignee.from || !assignee.to}
            onClick={() =>
              ask({
                title: en ? "Change the executor?" : "Сменить исполнителя?",
                message: en
                  ? `All tasks of ${nameOf(assignees, assignee.from)} are assigned to ${nameOf(users, assignee.to)}. No emails are sent`
                  : `Все задачи исполнителя ${nameOf(assignees, assignee.from)} перейдут к ${nameOf(users, assignee.to)}. Письма о назначении не отправляются`,
                label: en ? "Change" : "Сменить",
                path: "/admin/assignees/reassign",
                body: { fromEmail: assignee.from, toEmail: assignee.to },
                done: en ? "Tasks updated" : "Изменено задач",
                reset: () => setAssignee({ from: "", to: "" }),
                destructive: false,
              })
            }
          >
            {en ? "Change" : "Сменить"}
          </Button>,
        )}
        {block(
          en ? "Archive all tasks" : "Заархивировать все задачи",
          en
            ? `Moves every task that is not archived yet to the archive (${tasksLabel(tools?.notArchived ?? 0)})`
            : `Переносит в архив все ещё не заархивированные задачи (${tasksLabel(tools?.notArchived ?? 0)})`,
          null,
          <Button
            variant="outlined"
            sx={outlinedButtonSx}
            disabled={busy || !tools?.notArchived}
            onClick={() =>
              ask({
                title: en ? "Archive all tasks?" : "Заархивировать все задачи?",
                message: en
                  ? `${tools.notArchived} tasks will be moved to the archive`
                  : `В архив будет перенесено задач: ${tools.notArchived}`,
                label: en ? "Archive" : "Заархивировать",
                path: "/admin/tasks/archive-all",
                done: en ? "Tasks archived" : "Заархивировано задач",
                destructive: false,
              })
            }
          >
            {en ? "Archive all" : "Заархивировать все"}
          </Button>,
        )}
        {block(
          en ? "Delete all tasks" : "Удалить все задачи",
          en
            ? `Moves every task to “Deleted tasks”, where it can be restored (${tasksLabel(tools?.total ?? 0)})`
            : `Переносит все задачи в «Удалённые задачи», откуда их можно восстановить (${tasksLabel(tools?.total ?? 0)})`,
          null,
          <Button
            variant="contained"
            color="error"
            sx={destructiveButtonSx}
            disabled={busy || !tools?.total}
            onClick={() =>
              ask({
                title: en ? "Delete all tasks?" : "Удалить все задачи?",
                message: en
                  ? `${tools.total} tasks will be moved to “Deleted tasks”`
                  : `В «Удалённые задачи» будет перенесено задач: ${tools.total}`,
                label: en ? "Delete all" : "Удалить все",
                path: "/admin/tasks/delete-all",
                done: en ? "Tasks deleted" : "Удалено задач",
                destructive: true,
              })
            }
          >
            {en ? "Delete all" : "Удалить все"}
          </Button>,
        )}
      </Stack>
      <ConfirmActionDialog
        open={!!confirm}
        title={confirm?.title || ""}
        message={confirm?.message || ""}
        confirmLabel={confirm?.label || ""}
        destructive={!!confirm?.destructive}
        onClose={() => setConfirm(null)}
        onConfirm={run}
        busy={busy}
        currentLocale={currentLocale}
        currentTheme={currentTheme}
      />
    </Paper>
  );
}

function UsersSection(props) {
  const {
    currentLocale,
    currentUserEmail,
    currentRole,
    users,
    loading,
    onOpenNewUser,
    onRoleChange,
    onOpenPasswordDialog,
    onOpenDeleteDialog,
    onOpenProfile,
  } = props;
  const avatarBg = "ui.avatar";
  return (
    <Paper
      elevation={0}
      sx={{
        ...panelSx,
        width: "100%",
        maxWidth: { xs: "100%", xl: 1180 },
        mx: "auto",
      }}
    >
      <Box
        sx={{
          ...contentColumnSx,
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: 2,
          mb: 2,
        }}
      >
        <Box>
          <Typography variant="h6" sx={sectionTitleSx}>
            {currentLocale === "en" ? "Users" : "Пользователи"}
          </Typography>
          <Typography variant="body2" sx={sectionLeadSx}>
            {currentLocale === "en"
              ? "User accounts, roles, and password management"
              : "Список учетных записей, роли доступа и управление паролями"}
          </Typography>
        </Box>
        <Tooltip
          title={currentLocale === "en" ? "New user" : "Новый пользователь"}
          arrow
        >
          <IconButton
            onClick={onOpenNewUser}
            sx={{
              ...iconButtonSx,
              borderRadius: 999,
              color: "text.primary",
            }}
          >
            <AddIcon />
          </IconButton>
        </Tooltip>
      </Box>
      <Stack spacing={1.5} sx={contentColumnSx}>
        {users.map((user, index) => {
          const isCurrentUser =
            (user.email || "").toLowerCase() ===
            (currentUserEmail || "").toLowerCase();
          const displayName = (user.displayName || "").trim() || user.email;
          const initials = displayName.trim().charAt(0).toUpperCase() || "U";
          return (
            <Box
              key={user.email}
              component={motion.div}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: staggerDelay(index), duration: uiMotion.base, ease: uiMotion.ease }}
              sx={{
                ...blockSx,
                borderRadius: 3,
                p: { xs: 1.25, sm: 1.5 },
                bgcolor: "background.default",
              }}
            >
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1.5}
                justifyContent="space-between"
                alignItems={{ xs: "stretch", md: "center" }}
              >
                <Stack
                  direction="row"
                  spacing={1.25}
                  alignItems="flex-start"
                  sx={{ minWidth: 0, flex: 1 }}
                >
                  <Avatar
                    src={user.avatarUrl || ""}
                    sx={{
                      width: { xs: 40, sm: 44 },
                      height: { xs: 40, sm: 44 },
                      bgcolor: avatarBg,
                      color: "#fff",
                      fontSize: 16,
                      fontWeight: 700,
                      flexShrink: 0,
                    }}
                  >
                    {initials}
                  </Avatar>
                  <Box sx={{ minWidth: 0, flex: 1 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        lineHeight: 1.2,
                        wordBreak: "break-word",
                      }}
                    >
                      {displayName}
                    </Typography>
                    <Typography
                      variant="body2"
                      sx={{
                        color: "text.secondary",
                        mt: 0.35,
                        fontSize: { xs: 13, sm: 14 },
                        lineHeight: 1.35,
                        wordBreak: "break-word",
                      }}
                    >
                      {user.email}
                    </Typography>
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mt: 0.85, flexWrap: "wrap", rowGap: 0.75 }}
                    >
                      <Chip
                        label={roleLabel(user.role, currentLocale)}
                        size="small"
                        color={user.role === "admin" ? "primary" : "default"}
                      />
                      {user.status === "pending" ? (
                        <Chip
                          label={
                            currentLocale === "en" ? "Pending" : "Ожидается"
                          }
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      ) : null}
                      <Tooltip
                        title={
                          currentLocale === "en"
                            ? "Last sign in"
                            : "Последний вход"
                        }
                        arrow
                      >
                        <Typography
                          variant="caption"
                          sx={{
                            color: "text.secondary",
                            lineHeight: 1.35,
                            cursor: "help",
                          }}
                        >
                          {user.lastLoginAt
                            ? new Date(user.lastLoginAt).toLocaleString()
                            : "-"}
                        </Typography>
                      </Tooltip>
                    </Stack>
                  </Box>
                </Stack>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={1}
                  sx={{
                    width: { xs: "100%", md: "auto" },
                    alignItems: { xs: "stretch", sm: "center" },
                  }}
                >
                  <Tooltip
                    title={
                      isCurrentUser
                        ? t("admin.cannotChangeOwnRole", currentLocale)
                        : t("common.role", currentLocale)
                    }
                    arrow
                  >
                    <span>
                      <FormControl
                        size="small"
                        sx={{ width: { xs: "100%", sm: 172 } }}
                      >
                        <InputLabel>
                          {currentLocale === "en" ? "Role" : "Роль"}
                        </InputLabel>
                        <Select
                          label={currentLocale === "en" ? "Role" : "Роль"}
                          value={user.role}
                          onChange={(event) =>
                            onRoleChange(user.email, event.target.value)
                          }
                          disabled={isCurrentUser}
                        >
                          {roleOptions(currentRole).map((role) => (
                            <MenuItem key={role} value={role}>{roleLabel(role, currentLocale)}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </span>
                  </Tooltip>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ width: { xs: "100%", sm: "auto" } }}
                  >
                    <Tooltip title={currentLocale === "en" ? "Edit" : "Редактировать"} arrow>
                      <span style={{ display: "flex", width: "100%" }}>
                        <IconButton
                          onClick={() => onOpenProfile?.(user.email)}
                          disabled={loading}
                          sx={{
                            ...iconButtonSx,
                            width: { xs: "100%", sm: 42 },
                          }}
                        >
                          <EditOutlinedIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        isCurrentUser
                          ? t(
                              "admin.cannotChangeOwnPasswordHere",
                              currentLocale,
                            )
                          : currentLocale === "en"
                            ? "Change password"
                            : "Сменить пароль"
                      }
                      arrow
                    >
                      <span style={{ display: "flex", width: "100%" }}>
                        <IconButton
                          onClick={() => onOpenPasswordDialog(user.email)}
                          disabled={
                            loading ||
                            isCurrentUser ||
                            user.status === "pending"
                          }
                          sx={{
                            ...iconButtonSx,
                            borderRadius: 999,
                            color: "text.primary",
                            width: { xs: "100%", sm: 42 },
                            height: 42,
                          }}
                        >
                          <LockResetIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip
                      title={
                        isCurrentUser
                          ? t("admin.cannotDeleteOwnAccount", currentLocale)
                          : currentLocale === "en"
                            ? "Delete user"
                            : "Удалить пользователя"
                      }
                      arrow
                    >
                      <span style={{ display: "flex", width: "100%" }}>
                        <IconButton
                          onClick={() => onOpenDeleteDialog(user.email)}
                          disabled={loading || isCurrentUser}
                          sx={{
                            ...dangerIconButtonSx,
                            borderRadius: 999,
                            color: "error.main",
                            width: { xs: "100%", sm: 42 },
                            height: 42,
                          }}
                        >
                          <DeleteOutlineIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Stack>
                </Stack>
              </Stack>
            </Box>
          );
        })}
      </Stack>
    </Paper>
  );
}

function SecuritySection({
  currentLocale,
  currentTheme = "light",
  loading,
  allowRememberMe,
  enforceTwoFactor,
  sessionDurationMinutes,
  onSecuritySettingsChange,
  onSecuritySettingsSave,
}) {
  const isDark = currentTheme === "dark";
  return (
    <Paper
      elevation={0}
      sx={{
        ...panelSx,
        width: "100%",
        maxWidth: { xs: "100%", xl: 1180 },
        mx: "auto",
      }}
    >
      <Typography variant="h6" sx={sectionTitleSx}>
        {currentLocale === "en" ? "Security" : "Безопасность"}
      </Typography>
      <Typography variant="body2" sx={sectionLeadSx}>
        {currentLocale === "en"
          ? "Configure persistent sign-in and the lifetime of regular sessions"
          : "Настройте постоянный вход и время жизни обычной сессии"}
      </Typography>
      <Stack spacing={2} sx={contentColumnSx}>
        <Box sx={formBlockSx}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1.5}
          >
            <Box sx={{ pr: { sm: 2 }, maxWidth: 760 }}>
              <Typography sx={{ fontWeight: 600 }}>
                {currentLocale === "en"
                  ? "Allow users to stay signed in"
                  : "Разрешить запоминание входа"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {currentLocale === "en"
                  ? "Users will be able to keep their sign-in between browser sessions"
                  : "Пользователи смогут сохранять вход между сессиями браузера"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", minHeight: 40 }}>
              {loading ? (
                <CircularProgress size={20} sx={{ color: "text.secondary" }} />
              ) : (
                <Switch
                  checked={!!allowRememberMe}
                  onChange={(event) => {
                    onSecuritySettingsChange?.(
                      "allowRememberMe",
                      event.target.checked,
                    );
                    onSecuritySettingsSave?.({
                      allowRememberMe: event.target.checked,
                      sessionDurationMinutes,
                    });
                  }}
                />
              )}
            </Box>
          </Stack>
        </Box>
        <Box sx={formBlockSx}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", sm: "center" }}
            spacing={1.5}
          >
            <Box sx={{ pr: { sm: 2 }, maxWidth: 760 }}>
              <Typography sx={{ fontWeight: 600 }}>
                {currentLocale === "en"
                  ? "Force two-factor authentication"
                  : "Форсировать двухфакторную авторизацию"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {currentLocale === "en"
                  ? "Require an email verification code for every user sign-in"
                  : "Требовать код подтверждения по почте для входа всех пользователей"}
              </Typography>
            </Box>
            <Box sx={{ display: "flex", alignItems: "center", minHeight: 40 }}>
              {loading ? (
                <CircularProgress size={20} sx={{ color: "text.secondary" }} />
              ) : (
                <Switch
                  checked={!!enforceTwoFactor}
                  onChange={(event) => {
                    onSecuritySettingsChange?.(
                      "enforceTwoFactor",
                      event.target.checked,
                    );
                    onSecuritySettingsSave?.({
                      enforceTwoFactor: event.target.checked,
                      allowRememberMe,
                      sessionDurationMinutes,
                    });
                  }}
                />
              )}
            </Box>
          </Stack>
        </Box>
        <Box sx={formBlockSx}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                {currentLocale === "en"
                  ? "Session timeout without remember me"
                  : "Таймаут сессии без запоминания входа"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {currentLocale === "en"
                  ? "Determines how long a normal session remains active when the user does not choose persistent sign-in"
                  : "Определяет, сколько живёт сессия, если пользователь не выбрал запоминание входа"}
              </Typography>
            </Box>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              spacing={1.25}
              alignItems={{ xs: "stretch", sm: "flex-end" }}
            >
              <Box sx={formFieldWrapSx}>
                <TextField
                  label={currentLocale === "en" ? "Minutes" : "Минуты"}
                  type="number"
                  value={sessionDurationMinutes ?? ""}
                  onChange={(event) =>
                    onSecuritySettingsChange?.(
                      "sessionDurationMinutes",
                      event.target.value,
                    )
                  }
                  inputProps={{ min: 1, max: 10080 }}
                  sx={{ ...baseFieldSx(isDark), ...noSpinnerSx }}
                  fullWidth
                />
              </Box>
              <Button
                type="button"
                variant="contained"
                onClick={() =>
                  onSecuritySettingsSave?.({
                    allowRememberMe,
                    enforceTwoFactor,
                    sessionDurationMinutes,
                  })
                }
                disabled={loading}
                sx={primaryButtonSx}
              >
                {currentLocale === "en" ? "Apply" : "Применить"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

function GeneralSection({
  currentLocale,
  currentTheme = "light",
  loading,
  defaultTheme,
  defaultLocale,
  settings,
  onSecuritySettingsChange,
  onSecuritySettingsSave,
}) {
  const isDark = currentTheme === "dark";
  const en = currentLocale === "en";
  return (
    <Paper
      elevation={0}
      sx={{
        ...panelSx,
        width: "100%",
        maxWidth: { xs: "100%", xl: 1180 },
        mx: "auto",
      }}
    >
      <Typography variant="h6" sx={sectionTitleSx}>
        {currentLocale === "en" ? "General" : "Основные"}
      </Typography>
      <Typography variant="body2" sx={sectionLeadSx}>
        {currentLocale === "en"
          ? "Default appearance and language for the login screen and new users"
          : "Язык и тема по умолчанию для формы входа и новых пользователей"}
      </Typography>
      <Stack spacing={2} sx={contentColumnSx}>
        <Box sx={formBlockSx}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                {currentLocale === "en"
                  ? "Default language"
                  : "Язык по умолчанию"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {currentLocale === "en"
                  ? "Used on the login screen and assigned to newly created users"
                  : "Используется на форме входа и назначается новым пользователям"}
              </Typography>
            </Box>
            <Box sx={formFieldWrapSx}>
              <FormControl fullWidth sx={baseFieldSx(isDark)}>
                <InputLabel>
                  {currentLocale === "en" ? "Language" : "Язык"}
                </InputLabel>
                <Select
                  label={currentLocale === "en" ? "Language" : "Язык"}
                  value={defaultLocale || "ru"}
                  onChange={(event) => {
                    const value = event.target.value;
                    onSecuritySettingsChange?.("defaultLocale", value);
                    onSecuritySettingsSave?.({
                      defaultLocale: value,
                      defaultTheme,
                    });
                  }}
                  disabled={loading}
                >
                  <MenuItem value="ru">Русский</MenuItem>
                  <MenuItem value="en">English</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </Box>
        <Box sx={formBlockSx}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                {currentLocale === "en" ? "Default theme" : "Тема по умолчанию"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {currentLocale === "en"
                  ? "Applied to the login screen and to accounts created later"
                  : "Применяется к форме входа и к учётным записям, созданным позже"}
              </Typography>
            </Box>
            <Box sx={formFieldWrapSx}>
              <FormControl fullWidth sx={baseFieldSx(isDark)}>
                <InputLabel>
                  {currentLocale === "en" ? "Theme" : "Тема"}
                </InputLabel>
                <Select
                  label={currentLocale === "en" ? "Theme" : "Тема"}
                  value={defaultTheme || "light"}
                  onChange={(event) => {
                    const value = event.target.value;
                    onSecuritySettingsChange?.("defaultTheme", value);
                    onSecuritySettingsSave?.({
                      defaultTheme: value,
                      defaultLocale,
                    });
                  }}
                  disabled={loading}
                >
                  <MenuItem value="system">
                    {currentLocale === "en" ? "System" : "Системная"}
                  </MenuItem>
                  <MenuItem value="light">
                    {currentLocale === "en" ? "Light" : "Светлая"}
                  </MenuItem>
                  <MenuItem value="dark">
                    {currentLocale === "en" ? "Dark" : "Тёмная"}
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </Box>
        <Box sx={formBlockSx}>
          <FormControlLabel
            sx={{ m: 0, width: "100%", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}
            labelPlacement="start"
            control={
              <Switch
                checked={settings?.languageSwitcherVisible !== false}
                disabled={loading}
                onChange={(event) => {
                  const value = event.target.checked;
                  onSecuritySettingsChange?.("languageSwitcherVisible", value);
                  onSecuritySettingsSave?.({ languageSwitcherVisible: value });
                }}
              />
            }
            label={
              <Box>
                <Typography sx={{ fontWeight: 600 }}>
                  {en ? "Language switch button" : "Кнопка смены языка"}
                </Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                  {en
                    ? "Shows the language switch button in the header to all users"
                    : "Показывает всем пользователям кнопку смены языка в шапке"}
                </Typography>
              </Box>
            }
          />
        </Box>
      </Stack>
    </Paper>
  );
}

function InterfaceSection({
  currentLocale,
  currentTheme = "light",
  loading,
  settings,
  onSecuritySettingsChange,
  onSecuritySettingsSave,
}) {
  const en = currentLocale === "en";
  const items = [
    {
      key: "taskStatisticsVisible",
      label: en ? "Task statistics" : "Отображение статистики задач",
      description: en
        ? "Shows task statistics and the show/hide statistics button to all users"
        : "Показывает всем пользователям статистику задач и кнопку её скрытия или отображения",
    },
    {
      key: "taskFilterAssignedVisible",
      label: en ? "“Assigned” filter button" : "Кнопка фильтра «Назначенные»",
      description: en
        ? "Shows all users the button that hides or shows assigned tasks"
        : "Показывает всем пользователям кнопку скрытия и отображения назначенных задач",
    },
    {
      key: "taskFilterInProgressVisible",
      label: en ? "“In progress” filter button" : "Кнопка фильтра «В работе»",
      description: en
        ? "Shows all users the button that hides or shows tasks in progress"
        : "Показывает всем пользователям кнопку скрытия и отображения задач в работе",
    },
    {
      key: "taskFilterCompletedVisible",
      label: en ? "“Completed” filter button" : "Кнопка фильтра «Выполненные»",
      description: en
        ? "Shows all users the button that hides or shows completed tasks"
        : "Показывает всем пользователям кнопку скрытия и отображения выполненных задач",
    },
    {
      key: "taskFilterAssigneeVisible",
      label: en ? "Executor filter" : "Фильтр по исполнителям",
      description: en
        ? "Shows all users the executor filter next to the section filter"
        : "Показывает всем пользователям фильтр по исполнителям рядом с фильтром по разделам",
    },
  ];
  const isDark = currentTheme === "dark";
  return (
    <Paper elevation={0} sx={{ ...panelSx, width: "100%", maxWidth: { xs: "100%", xl: 1180 }, mx: "auto" }}>
      <Typography variant="h6" sx={sectionTitleSx}>{en ? "Interface" : "Интерфейс"}</Typography>
      <Typography variant="body2" sx={sectionLeadSx}>
        {en ? "Global dashboard interface settings for all users" : "Глобальные настройки интерфейса дашборда для всех пользователей"}
      </Typography>
      <Stack spacing={2} sx={contentColumnSx}>
        {items.map((item) => (
          <Box key={item.key} sx={formBlockSx}>
            <FormControlLabel
              sx={{ m: 0, width: "100%", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}
              labelPlacement="start"
              control={
                <Switch
                  checked={settings?.[item.key] !== false}
                  disabled={loading}
                  onChange={(event) => {
                    const value = event.target.checked;
                    onSecuritySettingsChange?.(item.key, value);
                    onSecuritySettingsSave?.({ [item.key]: value });
                  }}
                />
              }
              label={
                <Box>
                  <Typography sx={{ fontWeight: 600 }}>{item.label}</Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>{item.description}</Typography>
                </Box>
              }
            />
          </Box>
        ))}
        <Box sx={formBlockSx}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                {en ? "Default task list view" : "Вид списка задач по умолчанию"}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                {en
                  ? "Used until a user switches the view themselves"
                  : "Действует, пока пользователь сам не переключит вид списка"}
              </Typography>
            </Box>
            <Box sx={formFieldWrapSx}>
              <FormControl fullWidth sx={baseFieldSx(isDark)}>
                <InputLabel>{en ? "View" : "Вид"}</InputLabel>
                <Select
                  label={en ? "View" : "Вид"}
                  value={settings?.taskListView || "expanded"}
                  disabled={loading}
                  onChange={(event) => {
                    const value = event.target.value;
                    onSecuritySettingsChange?.("taskListView", value);
                    onSecuritySettingsSave?.({ taskListView: value });
                  }}
                >
                  <MenuItem value="expanded">{en ? "Expanded" : "Расширенный"}</MenuItem>
                  <MenuItem value="compact">{en ? "Compact" : "Компактный"}</MenuItem>
                </Select>
              </FormControl>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

function EnvironmentSection({
  currentLocale,
  currentTheme = "light",
  loading,
  settings,
  onSecuritySettingsChange,
  onSecuritySettingsSave,
}) {
  const en = currentLocale === "en";
  const current = sectionTermsFor(settings?.sectionTerm, "ru").plural;
  const savedTerms = sectionTermsFor(settings?.sectionTerm, currentLocale);
  const chooseTerm = (value) => {
    if (value === current) return;
    onSecuritySettingsChange?.("sectionTerm", value);
    onSecuritySettingsSave?.({ sectionTerm: value });
  };
  return (
    <Paper elevation={0} sx={{ ...panelSx, width: "100%", maxWidth: { xs: "100%", xl: 1180 }, mx: "auto" }}>
      <Typography variant="h6" sx={sectionTitleSx}>{en ? "Environment" : "Окружение"}</Typography>
      <Typography variant="body2" sx={sectionLeadSx}>
        {en ? "Working environment settings" : "Настройка рабочего окружения"}
      </Typography>
      <Stack spacing={2} sx={contentColumnSx}>
        <Box sx={formBlockSx}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                {en ? "Task grouping" : "Разделение задач"}
              </Typography>
              <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                {en
                  ? "Logical grouping of tasks used in this environment"
                  : "Логическое разделение задач, используемое в этом окружении"}
              </Typography>
            </Box>
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {SECTION_TERM_OPTIONS.map((option) => (
                <Chip
                  key={option.value}
                  label={en ? option.en : option.value}
                  variant={option.value === current ? "filled" : "outlined"}
                  color={option.value === current ? "primary" : "default"}
                  onClick={() => chooseTerm(option.value)}
                  disabled={loading}
                />
              ))}
            </Stack>
          </Stack>
        </Box>
        <Box sx={formBlockSx}>
          <FormControlLabel
            sx={{ m: 0, width: "100%", alignItems: "flex-start", justifyContent: "space-between", gap: 2 }}
            labelPlacement="start"
            control={
              <Switch
                checked={settings?.defaultSectionEnabled !== false}
                disabled={loading}
                onChange={(event) => {
                  const value = event.target.checked;
                  onSecuritySettingsChange?.("defaultSectionEnabled", value);
                  onSecuritySettingsSave?.({ defaultSectionEnabled: value });
                }}
              />
            }
            label={
              <Box>
                <Typography sx={{ fontWeight: 600 }}>{savedTerms.defaultLabel}</Typography>
                <Typography variant="body2" sx={{ color: "text.secondary", mt: 0.5 }}>
                  {savedTerms.allowDefault}
                </Typography>
              </Box>
            }
          />
        </Box>
      </Stack>
    </Paper>
  );
}

function EmailSection({
  currentLocale,
  currentTheme = "light",
  loading,
  settings,
  onSecuritySettingsChange,
  onSecuritySettingsSave,
  onCheckEmailSettings,
  onOpenTestEmailDialog,
}) {
  const isDark = currentTheme === "dark";
  const [showPassword, setShowPassword] = useState(false);
  return (
    <Paper
      elevation={0}
      sx={{
        ...panelSx,
        width: "100%",
        maxWidth: { xs: "100%", xl: 1180 },
        mx: "auto",
      }}
    >
      <Typography variant="h6" sx={sectionTitleSx}>
        {currentLocale === "en" ? "Email" : "Эл. почта"}
      </Typography>
      <Typography variant="body2" sx={sectionLeadSx}>
        {currentLocale === "en"
          ? "Configure SMTP connection settings. These values override the environment defaults"
          : "Настройте параметры SMTP-подключения. Эти значения переопределяют настройки из env"}
      </Typography>
      <Stack spacing={2} sx={contentColumnSx}>
        <Box sx={formBlockSx}>
          <Stack spacing={1.75}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                {currentLocale === "en"
                  ? "Protocol settings"
                  : "Настройка протокола"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {currentLocale === "en"
                  ? "Connection parameters for the SMTP server"
                  : "Параметры подключения к SMTP-серверу"}
              </Typography>
            </Box>
            <Box sx={formFieldWrapSx}>
              <TextField
                label="SMTP Host"
                value={settings.smtpHost || ""}
                onChange={(event) =>
                  onSecuritySettingsChange?.("smtpHost", event.target.value)
                }
                fullWidth
                sx={baseFieldSx(isDark)}
              />
            </Box>
            <Box sx={formFieldWrapSx}>
              <TextField
                label="SMTP Port"
                type="number"
                value={settings.smtpPort ?? ""}
                onChange={(event) =>
                  onSecuritySettingsChange?.("smtpPort", event.target.value)
                }
                fullWidth
                sx={{ ...baseFieldSx(isDark), ...noSpinnerSx }}
              />
            </Box>
            <Box sx={formFieldWrapSx}>
              <FormControl fullWidth sx={baseFieldSx(isDark)}>
                <InputLabel>
                  {currentLocale === "en"
                    ? "Connection security"
                    : "Защита соединения"}
                </InputLabel>
                <Select
                  label={
                    currentLocale === "en"
                      ? "Connection security"
                      : "Защита соединения"
                  }
                  value={settings.smtpSecurity || "starttls"}
                  onChange={(event) =>
                    onSecuritySettingsChange?.(
                      "smtpSecurity",
                      event.target.value,
                    )
                  }
                >
                  <MenuItem value="starttls">STARTTLS</MenuItem>
                  <MenuItem value="ssl">SSL/TLS</MenuItem>
                  <MenuItem value="none">
                    {currentLocale === "en" ? "No TLS" : "Без TLS"}
                  </MenuItem>
                </Select>
              </FormControl>
            </Box>
            <Box sx={formFieldWrapSx}>
              <TextField
                label="SMTP User"
                value={settings.smtpUser || ""}
                onChange={(event) =>
                  onSecuritySettingsChange?.("smtpUser", event.target.value)
                }
                fullWidth
                sx={baseFieldSx(isDark)}
              />
            </Box>
            <Box sx={formFieldWrapSx}>
              <TextField
                label="SMTP Password"
                type={showPassword ? "text" : "password"}
                value={settings.smtpPassword || ""}
                onChange={(event) =>
                  onSecuritySettingsChange?.("smtpPassword", event.target.value)
                }
                fullWidth
                sx={baseFieldSx(isDark)}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        onClick={() => setShowPassword((prev) => !prev)}
                        edge="end"
                      >
                        {showPassword ? (
                          <VisibilityOffOutlinedIcon />
                        ) : (
                          <VisibilityOutlinedIcon />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
            <Box
              sx={{
                ...formFieldWrapSx,
                display: "flex",
                justifyContent: "flex-end",
              }}
            >
              <Button
                type="button"
                variant="outlined"
                onClick={() => onCheckEmailSettings?.()}
                disabled={loading}
                sx={outlinedButtonSx}
              >
                {currentLocale === "en" ? "Check" : "Проверить"}
              </Button>
            </Box>
          </Stack>
        </Box>
        <Box sx={formBlockSx}>
          <Stack spacing={1.75}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                {currentLocale === "en"
                  ? "Sender settings"
                  : "Параметры отправителя"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {currentLocale === "en"
                  ? "The address and name that recipients will see"
                  : "Адрес и имя, которые увидит получатель письма"}
              </Typography>
            </Box>
            <Box sx={formFieldWrapSx}>
              <TextField
                label={
                  currentLocale === "en" ? "From email" : "Email отправителя"
                }
                value={settings.smtpFromEmail || ""}
                onChange={(event) =>
                  onSecuritySettingsChange?.(
                    "smtpFromEmail",
                    event.target.value,
                  )
                }
                fullWidth
                sx={baseFieldSx(isDark)}
              />
            </Box>
            <Box sx={formFieldWrapSx}>
              <TextField
                label={currentLocale === "en" ? "From name" : "Имя отправителя"}
                value={settings.smtpFromName || ""}
                onChange={(event) =>
                  onSecuritySettingsChange?.("smtpFromName", event.target.value)
                }
                fullWidth
                sx={baseFieldSx(isDark)}
              />
            </Box>
            <Box
              sx={{
                ...formFieldWrapSx,
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Button
                type="button"
                variant="outlined"
                onClick={() => onOpenTestEmailDialog?.()}
                disabled={loading}
                sx={outlinedButtonSx}
              >
                {currentLocale === "en"
                  ? "Send test email"
                  : "Отправить тестовое"}
              </Button>
              <Button
                type="button"
                variant="contained"
                onClick={() => onSecuritySettingsSave?.()}
                disabled={loading}
                sx={primaryButtonSx}
              >
                {currentLocale === "en"
                  ? "Save email settings"
                  : "Сохранить настройки почты"}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

function NotificationsSection({ currentLocale, currentTheme = "light", settings, onSecuritySettingsChange, onSecuritySettingsSave }) {
  const en = currentLocale === "en";
  const kinds = [
    ["taskNewNotificationsEnabled", en ? "New tasks" : "Новые задачи"],
    ["taskStatusNotificationsEnabled", en ? "Status changes" : "Изменение статуса"],
    ["taskDeadlineNotificationsEnabled", en ? "Approaching deadlines" : "Приближение сроков"],
  ];
  return (
    <Paper elevation={0} sx={{ ...panelSx, width: "100%", maxWidth: { xs: "100%", xl: 1180 }, mx: "auto" }}>
      <Typography variant="h6" sx={sectionTitleSx}>{en ? "Notifications" : "Уведомления"}</Typography>
      <Typography variant="body2" sx={sectionLeadSx}>{en ? "Choose delivery providers and configure notification types" : "Выберите провайдеры доставки и настройте типы уведомлений"}</Typography>
      <Stack spacing={2}>
        <Box sx={formBlockSx}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>{en ? "Delivery providers" : "Провайдеры уведомлений"}</Typography>
          <FormControlLabel control={<Switch checked={settings.emailNotificationsEnabled ?? true} onChange={(e) => onSecuritySettingsChange("emailNotificationsEnabled", e.target.checked)} />} label={en ? "Email" : "Электронная почта"} />
        </Box>
        <Box sx={formBlockSx}>
          <Typography sx={{ fontWeight: 700, mb: 1 }}>{en ? "Notification types" : "Типы уведомлений"}</Typography>
          {kinds.map(([globalKey, label]) => <FormControlLabel key={globalKey} control={<Switch checked={settings[globalKey] ?? true} onChange={(e) => onSecuritySettingsChange(globalKey, e.target.checked)} />} label={label} />)}
          <Box sx={{ mt: 1 }}><Button variant="contained" sx={primaryButtonSx} onClick={() => onSecuritySettingsSave()}>{en ? "Save" : "Сохранить"}</Button></Box>
        </Box>
      </Stack>
    </Paper>
  );
}

function TemplatesSection({
  currentLocale,
  currentTheme = "light",
  loading,
  settings,
  onSecuritySettingsChange,
  onSecuritySettingsSave,
}) {
  const isDark = currentTheme === "dark";
  return (
    <Paper
      elevation={0}
      sx={{
        ...panelSx,
        width: "100%",
        maxWidth: { xs: "100%", xl: 1180 },
        mx: "auto",
      }}
    >
      <Typography variant="h6" sx={sectionTitleSx}>
        {currentLocale === "en"
          ? "Notification templates"
          : "Шаблоны уведомлений"}
      </Typography>
      <Typography variant="body2" sx={sectionLeadSx}>
        {currentLocale === "en"
          ? "Manage email templates for future notifications"
          : "Управляйте шаблонами писем для текущих и будущих уведомлений"}
      </Typography>
      <Stack spacing={2} sx={contentColumnSx}>
        {[
          ["taskNewSubject", "taskNewBodyTemplate", currentLocale === "en" ? "New task" : "Новая задача", "{title}, {description}, {due_at}, {link}, {task_id}"],
          ["taskStatusSubject", "taskStatusBodyTemplate", currentLocale === "en" ? "Status change" : "Изменение статуса", "{title}, {description}, {old_status}, {new_status}, {resolution}, {link}, {task_id}"],
          ["taskDeadlineSubject", "taskDeadlineBodyTemplate", currentLocale === "en" ? "Approaching deadline" : "Приближение срока", "{title}, {due_at}, {link}, {task_id}"],
        ].map(([subjectKey, bodyKey, title, variables]) => (
          <Box sx={formBlockSx} key={subjectKey}>
            <Stack spacing={1.5}>
              <Typography sx={{ fontWeight: 600 }}>{title}</Typography>
              <TextField label={currentLocale === "en" ? "Email subject" : "Тема письма"} value={settings[subjectKey] || ""} onChange={(e) => onSecuritySettingsChange(subjectKey, e.target.value)} fullWidth />
              <TextField label={currentLocale === "en" ? "Email body" : "Текст письма"} value={settings[bodyKey] || ""} onChange={(e) => onSecuritySettingsChange(bodyKey, e.target.value)} multiline minRows={4} fullWidth helperText={`${currentLocale === "en" ? "Variables" : "Переменные"}: ${variables}${/\{(description|resolution)\}/.test(variables) ? (currentLocale === "en" ? ". A line with an empty {description} or {resolution} is left out of the email" : ". Строка с пустым {description} или {resolution} в письмо не попадает") : ""}`} />
              <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
                <Button
                  type="button"
                  variant="contained"
                  onClick={() => onSecuritySettingsSave?.()}
                  disabled={loading}
                  sx={primaryButtonSx}
                >
                  {currentLocale === "en" ? "Save template" : "Сохранить шаблон"}
                </Button>
              </Box>
            </Stack>
          </Box>
        ))}
        <Box sx={formBlockSx}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                {currentLocale === "en"
                  ? "Password reset email"
                  : "Письмо восстановления пароля"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {currentLocale === "en"
                  ? "Template for the password reset message"
                  : "Шаблон письма для восстановления пароля"}
              </Typography>
            </Box>
            <Box sx={formFieldWrapSx}>
              <TextField
                label={currentLocale === "en" ? "Email subject" : "Тема письма"}
                value={settings.passwordResetSubject || ""}
                onChange={(event) =>
                  onSecuritySettingsChange?.(
                    "passwordResetSubject",
                    event.target.value,
                  )
                }
                fullWidth
                sx={baseFieldSx(isDark)}
              />
            </Box>
            <Box sx={formFieldWrapSx}>
              <TextField
                label={currentLocale === "en" ? "Email body" : "Текст письма"}
                value={settings.passwordResetBodyTemplate || ""}
                onChange={(event) =>
                  onSecuritySettingsChange?.(
                    "passwordResetBodyTemplate",
                    event.target.value,
                  )
                }
                multiline
                minRows={5}
                fullWidth
                sx={baseFieldSx(isDark)}
                helperText={
                  currentLocale === "en"
                    ? "Use {code} where the reset code should appear"
                    : "Используйте {code} в месте, где должен подставляться код восстановления"
                }
              />
            </Box>
            <Box
              sx={{
                ...formFieldWrapSx,
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Button
                type="button"
                variant="contained"
                onClick={() => onSecuritySettingsSave?.()}
                disabled={loading}
                sx={primaryButtonSx}
              >
                {currentLocale === "en" ? "Save template" : "Сохранить шаблон"}
              </Button>
            </Box>
          </Stack>
        </Box>
        <Box sx={formBlockSx}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                {currentLocale === "en"
                  ? "Login verification / 2FA email"
                  : "Письмо подтверждения входа / 2FA"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {currentLocale === "en"
                  ? "Template for login confirmation and two-factor verification emails"
                  : "Шаблон письма для подтверждения входа и двухфакторной аутентификации"}
              </Typography>
            </Box>
            <Box sx={formFieldWrapSx}>
              <TextField
                label={currentLocale === "en" ? "Email subject" : "Тема письма"}
                value={settings.loginVerificationSubject || ""}
                onChange={(event) =>
                  onSecuritySettingsChange?.(
                    "loginVerificationSubject",
                    event.target.value,
                  )
                }
                fullWidth
                sx={baseFieldSx(isDark)}
              />
            </Box>
            <Box sx={formFieldWrapSx}>
              <TextField
                label={currentLocale === "en" ? "Email body" : "Текст письма"}
                value={settings.loginVerificationBodyTemplate || ""}
                onChange={(event) =>
                  onSecuritySettingsChange?.(
                    "loginVerificationBodyTemplate",
                    event.target.value,
                  )
                }
                multiline
                minRows={5}
                fullWidth
                sx={baseFieldSx(isDark)}
                helperText={
                  currentLocale === "en"
                    ? "Use {code} where the verification code should appear"
                    : "Используйте {code} в месте, где должен подставляться код подтверждения"
                }
              />
            </Box>
            <Box
              sx={{
                ...formFieldWrapSx,
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Button
                type="button"
                variant="contained"
                onClick={() => onSecuritySettingsSave?.()}
                disabled={loading}
                sx={primaryButtonSx}
              >
                {currentLocale === "en" ? "Save template" : "Сохранить шаблон"}
              </Button>
            </Box>
          </Stack>
        </Box>
        <Box sx={formBlockSx}>
          <Stack spacing={1.5}>
            <Box>
              <Typography sx={{ fontWeight: 600 }}>
                {currentLocale === "en"
                  ? "User invitation email"
                  : "Письмо-приглашение пользователя"}
              </Typography>
              <Typography
                variant="body2"
                sx={{ color: "text.secondary", mt: 0.5 }}
              >
                {currentLocale === "en"
                  ? "Sent when a user is created with an invitation link"
                  : "Отправляется при создании пользователя по ссылке-приглашению"}
              </Typography>
            </Box>
            <Box sx={formFieldWrapSx}>
              <TextField
                label={currentLocale === "en" ? "Email subject" : "Тема письма"}
                value={settings.invitationSubject || ""}
                onChange={(event) =>
                  onSecuritySettingsChange?.(
                    "invitationSubject",
                    event.target.value,
                  )
                }
                fullWidth
                sx={baseFieldSx(isDark)}
              />
            </Box>
            <Box sx={formFieldWrapSx}>
              <TextField
                label={currentLocale === "en" ? "Email body" : "Текст письма"}
                value={settings.invitationBodyTemplate || ""}
                onChange={(event) =>
                  onSecuritySettingsChange?.(
                    "invitationBodyTemplate",
                    event.target.value,
                  )
                }
                multiline
                minRows={5}
                fullWidth
                sx={baseFieldSx(isDark)}
                helperText={
                  currentLocale === "en"
                    ? "Use {link} for the invitation link and {email} for the recipient address"
                    : "Используйте {link} для ссылки-приглашения и {email} для адреса получателя"
                }
              />
            </Box>
            <Box
              sx={{
                ...formFieldWrapSx,
                display: "flex",
                justifyContent: "flex-end",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Button
                type="button"
                variant="contained"
                onClick={() => onSecuritySettingsSave?.()}
                disabled={loading}
                sx={primaryButtonSx}
              >
                {currentLocale === "en" ? "Save template" : "Сохранить шаблон"}
              </Button>
            </Box>
          </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}

export default function NewAdminPanelPage({
  activeSection = "general",
  currentLocale = "ru",
  currentTheme = "light",
  currentUserEmail,
  currentRole = "admin",
  securitySettings,
  onSectionChange,
  onBack,
  users = [],
  sections = [],
  sectionTerms,
  defaultSectionEnabled = true,
  onSectionsChanged,
  loading,
  createForm,
  passwordDialog,
  deleteUserDialog,
  allowRememberMe,
  sessionDurationMinutes,
  defaultTheme,
  defaultLocale,
  onSecuritySettingsChange,
  onSecuritySettingsSave,
  onCheckEmailSettings,
  onSendTestEmail,
  onProfilesImported,
  onOpenNewUser,
  onCloseNewUser,
  onCreateUserFieldChange,
  onCreateUser,
  onRoleChange,
  onOpenPasswordDialog,
  onClosePasswordDialog,
  onPasswordFieldChange,
  onPasswordConfirmChange,
  onPasswordSave,
  onOpenDeleteDialog,
  onOpenProfile,
  onCloseDeleteDialog,
  onConfirmDeleteUser,
  onNotify,
}) {
  const [testEmailDialogOpen, setTestEmailDialogOpen] = useState(false);
  const [testEmail, setTestEmail] = useState("");
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const isDark = currentTheme === "dark";
  const termedNavItems = navItems.map((item) =>
    item.key === "sections" && sectionTerms
      ? { ...item, label: { en: sectionTerms.plural, ru: sectionTerms.plural } }
      : item,
  );
  const visibleNavItems = currentRole === "admin"
    ? termedNavItems
    : termedNavItems.filter((item) => ["sections", "deleted-tasks"].includes(item.key));
  const activeNavItem = termedNavItems.find((entry) => entry.key === activeSection);
  const activeNavLabel = activeNavItem ? activeNavItem.label[currentLocale === "en" ? "en" : "ru"] : "";

  const renderSection = () => {
    if (activeSection === "general") {
      return (
        <GeneralSection
          currentLocale={currentLocale}
          currentTheme={currentTheme}
          loading={loading}
          defaultTheme={defaultTheme}
          defaultLocale={defaultLocale}
          settings={securitySettings || {}}
          onSecuritySettingsChange={onSecuritySettingsChange}
          onSecuritySettingsSave={onSecuritySettingsSave}
        />
      );
    }
    if (activeSection === "interface") {
      return (
        <InterfaceSection
          currentLocale={currentLocale}
          currentTheme={currentTheme}
          loading={loading}
          settings={securitySettings || {}}
          onSecuritySettingsChange={onSecuritySettingsChange}
          onSecuritySettingsSave={onSecuritySettingsSave}
        />
      );
    }
    if (activeSection === "environment") {
      return (
        <EnvironmentSection
          currentLocale={currentLocale}
          currentTheme={currentTheme}
          loading={loading}
          settings={securitySettings || {}}
          onSecuritySettingsChange={onSecuritySettingsChange}
          onSecuritySettingsSave={onSecuritySettingsSave}
        />
      );
    }
    if (activeSection === "security") {
      return (
        <SecuritySection
          currentLocale={currentLocale}
          currentTheme={currentTheme}
          loading={loading}
          allowRememberMe={allowRememberMe}
          enforceTwoFactor={securitySettings?.enforceTwoFactor}
          sessionDurationMinutes={sessionDurationMinutes}
          profileShareTtlMinutes={securitySettings?.profileShareTtlMinutes}
          onSecuritySettingsChange={onSecuritySettingsChange}
          onSecuritySettingsSave={onSecuritySettingsSave}
        />
      );
    }
    if (activeSection === "email") {
      return (
        <EmailSection
          currentLocale={currentLocale}
          currentTheme={currentTheme}
          loading={loading}
          settings={securitySettings || {}}
          onSecuritySettingsChange={onSecuritySettingsChange}
          onSecuritySettingsSave={onSecuritySettingsSave}
          onCheckEmailSettings={onCheckEmailSettings}
          onOpenTestEmailDialog={() => setTestEmailDialogOpen(true)}
        />
      );
    }
    if (activeSection === "templates") {
      return (
        <TemplatesSection
          currentLocale={currentLocale}
          currentTheme={currentTheme}
          loading={loading}
          settings={securitySettings || {}}
          onSecuritySettingsChange={onSecuritySettingsChange}
          onSecuritySettingsSave={onSecuritySettingsSave}
        />
      );
    }
    if (activeSection === "notifications") {
      return <NotificationsSection currentLocale={currentLocale} currentTheme={currentTheme} settings={securitySettings || {}} onSecuritySettingsChange={onSecuritySettingsChange} onSecuritySettingsSave={onSecuritySettingsSave} />;
    }
    if (activeSection === "sections") {
      return (
        <SectionsSection
          currentLocale={currentLocale}
          currentTheme={currentTheme}
          sections={sections}
          terms={sectionTerms || sectionTermsFor("", currentLocale)}
          defaultSectionEnabled={defaultSectionEnabled}
          onChanged={onSectionsChanged}
        />
      );
    }
    if (activeSection === "tasks" && currentRole === "admin") {
      return (
        <TasksSection
          currentLocale={currentLocale}
          currentTheme={currentTheme}
          users={users}
          terms={sectionTerms || sectionTermsFor("", currentLocale)}
          onTasksChanged={onSectionsChanged}
          onNotify={onNotify}
        />
      );
    }
    if (activeSection === "deleted-tasks") {
      return <DeletedTasksSection currentLocale={currentLocale} currentRole={currentRole} currentTheme={currentTheme} />;
    }
    if (currentRole !== "admin") return null;
    return (
      <UsersSection
        currentLocale={currentLocale}
        currentTheme={currentTheme}
        currentUserEmail={currentUserEmail}
        currentRole={currentRole}
        users={users}
        loading={loading}
        onOpenNewUser={onOpenNewUser}
        onRoleChange={onRoleChange}
        onOpenPasswordDialog={onOpenPasswordDialog}
        onOpenDeleteDialog={onOpenDeleteDialog}
        onOpenProfile={onOpenProfile}
      />
    );
  };

  return (
    <Box
      component={motion.div}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: uiMotion.base, ease: uiMotion.ease }}
      sx={{
        px: { xs: 1.5, sm: 2, md: 3 },
        pb: 3,
        mt: { xs: 2, md: 3 },
        flex: 1,
        display: "flex",
      }}
    >
      <Paper
        elevation={0}
        sx={{
          ...shellCardSx,
          p: { xs: 1.5, md: 2 },
          display: "flex",
          flexDirection: { xs: "column", minWidth: "row" },
          gap: 2.5,
          width: "100%",
          alignItems: "stretch",
          "@media (min-width:1100px)": { flexDirection: "row" },
          "@media (max-width:1099.98px)": { flexDirection: "column" },
        }}
      >
        <Box
          sx={{
            width: { xs: "100%", md: 260 },
            flexShrink: 0,
            display: "none",
            "@media (min-width:1100px)": { display: "block" },
          }}
        >
          <Paper elevation={0} sx={{ ...panelSx, p: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
              {currentLocale === "en" ? "Administration" : "Администрирование"}
            </Typography>
            <AdminNavList
              items={visibleNavItems}
              activeKey={activeSection}
              locale={currentLocale}
              layoutId="admin-nav-desktop"
              onSelect={onSectionChange}
            />
            <Box sx={{ mt: 2 }}>
              <Button
                onClick={onBack}
                variant="outlined"
                fullWidth
                startIcon={<ArrowBackIcon />}
                sx={outlinedButtonSx}
              >
                {currentLocale === "en" ? "Back" : "Назад"}
              </Button>
            </Box>
          </Paper>
        </Box>
        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            px: { xs: 0, md: 2, xl: 4 },
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              mb: 2,
              "@media (min-width:1100px)": { display: "none" },
            }}
          >
            <Button
              type="button"
              variant="outlined"
              onClick={() => setMobileNavOpen(true)}
              sx={{
                ...outlinedButtonSx,
                minHeight: 42,
              }}
            >
              {activeNavLabel}
            </Button>
          </Box>
          <Box
            sx={{ width: "100%", display: "flex", justifyContent: "center" }}
          >
            <AnimatePresence mode="wait" initial={false}>
              <Box
                key={activeSection}
                component={motion.div}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -8 }}
                transition={{ duration: uiMotion.fast, ease: uiMotion.ease }}
                sx={{ width: "100%" }}
              >
                {renderSection()}
              </Box>
            </AnimatePresence>
          </Box>
        </Box>
      </Paper>
      <Dialog
        open={mobileNavOpen}
        onClose={() => setMobileNavOpen(false)}
        fullWidth
        maxWidth="xs"
        sx={dialogSx}
      >
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {currentLocale === "en" ? "Sections" : "Разделы"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          <AdminNavList
            items={visibleNavItems}
            activeKey={activeSection}
            locale={currentLocale}
            layoutId="admin-nav-mobile"
            onSelect={(key) => {
              onSectionChange(key);
              setMobileNavOpen(false);
            }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setMobileNavOpen(false)}
            sx={secondaryButtonSx}
          >
            {t("common.cancel", currentLocale)}
          </Button>
        </DialogActions>
      </Dialog>
      <CreateUserDialog
        open={!!createForm?.open}
        currentLocale={currentLocale}
        currentTheme={currentTheme}
        form={createForm || {}}
        onClose={onCloseNewUser}
        onChange={onCreateUserFieldChange}
        onCreate={onCreateUser}
        loading={loading}
        currentRole={currentRole}
      />
      <PasswordDialog
        open={!!passwordDialog?.open}
        currentLocale={currentLocale}
        currentTheme={currentTheme}
        form={passwordDialog || {}}
        onClose={onClosePasswordDialog}
        onPasswordChange={onPasswordFieldChange}
        onConfirmChange={onPasswordConfirmChange}
        onSave={onPasswordSave}
        loading={loading}
      />
      <DeleteDialog
        open={!!deleteUserDialog?.open}
        currentLocale={currentLocale}
        currentTheme={currentTheme}
        email={deleteUserDialog?.email || ""}
        onClose={onCloseDeleteDialog}
        onConfirm={onConfirmDeleteUser}
        loading={loading}
      />
      <Dialog
        open={testEmailDialogOpen}
        onClose={loading ? undefined : () => setTestEmailDialogOpen(false)}
        fullWidth
        maxWidth="sm"
        sx={dialogSx}
      >
        <DialogTitle>
          <Typography variant="h5" sx={{ fontWeight: 700 }}>
            {currentLocale === "en"
              ? "Send test email"
              : "Отправить тестовое письмо"}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: "text.secondary", mt: 0.75 }}
          >
            {currentLocale === "en"
              ? "Enter the recipient email address for the test message"
              : "Введите адрес получателя для тестового письма"}
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 1.5 }}>
          <TextField
            label={
              currentLocale === "en" ? "Recipient email" : "Email получателя"
            }
            value={testEmail}
            onChange={(event) => setTestEmail(event.target.value)}
            fullWidth
            sx={{ ...baseFieldSx(isDark), mt: 0.5 }}
          />
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            onClick={() => setTestEmailDialogOpen(false)}
            disabled={loading}
            sx={secondaryButtonSx}
          >
            {t("common.cancel", currentLocale)}
          </Button>
          <Button
            onClick={async () => {
              await onSendTestEmail?.(testEmail);
              setTestEmailDialogOpen(false);
            }}
            variant="contained"
            disabled={loading || !testEmail.trim()}
            sx={primaryButtonSx}
          >
            {currentLocale === "en" ? "Send" : "Отправить"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
